import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Server connection status
const CONNECTION_STATUS = {
  CHECKING: 'checking',
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  FAILED: 'failed'
};

// Demo user data
const DEMO_USER = {
  id: 'demo-user',
  email: 'demo@traderelax.com',
  full_name: 'Demo Trader',
  role: 'USER',
  isDemo: true
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isDemo, setIsDemo] = useState(localStorage.getItem('demoMode') === 'true');
  const [serverStatus, setServerStatus] = useState(CONNECTION_STATUS.CHECKING);
  const [connectionAttempts, setConnectionAttempts] = useState(0);

  // Check server health with retries
  const checkServerHealth = useCallback(async (attempt = 1, maxAttempts = 15) => {
    setConnectionAttempts(attempt);
    
    try {
      const response = await axios.get(`${API}/health`, { 
        timeout: 5000,
        validateStatus: (status) => status === 200
      });
      
      if (response.data?.status === 'ok') {
        setServerStatus(CONNECTION_STATUS.CONNECTED);
        return true;
      }
    } catch (error) {
      console.log(`Server health check attempt ${attempt}/${maxAttempts} failed`);
    }
    
    if (attempt < maxAttempts) {
      setServerStatus(CONNECTION_STATUS.CONNECTING);
      // Exponential backoff: 2s, 3s, 4s, 5s... max 8s
      const delay = Math.min(2000 + (attempt * 1000), 8000);
      await new Promise(resolve => setTimeout(resolve, delay));
      return checkServerHealth(attempt + 1, maxAttempts);
    }
    
    setServerStatus(CONNECTION_STATUS.FAILED);
    return false;
  }, []);

  // Initial server check on mount
  useEffect(() => {
    const initializeConnection = async () => {
      // Check for demo mode first
      if (localStorage.getItem('demoMode') === 'true') {
        setUser(DEMO_USER);
        setIsDemo(true);
        setLoading(false);
        // Still check server health in background for demo
        checkServerHealth();
        return;
      }
      
      const isConnected = await checkServerHealth();
      
      if (isConnected && token) {
        await validateToken();
      } else {
        setLoading(false);
      }
    };
    
    initializeConnection();
  }, []);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setUser(response.data);
    } catch (error) {
      // Token invalid, clear it
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  };

  const login = async (email, password, retryCount = 0) => {
    // Ensure server is connected first
    if (serverStatus !== CONNECTION_STATUS.CONNECTED) {
      const isConnected = await checkServerHealth(1, 10);
      if (!isConnected) {
        throw new Error('Unable to connect to server. Please try again.');
      }
    }
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, {
        timeout: 25000 // 25 second timeout
      });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      return userData;
    } catch (error) {
      // Retry on network errors, timeouts, or 500+ errors (up to 5 retries)
      const isRetryableError = error.code === 'ERR_NETWORK' || 
                               error.code === 'ECONNABORTED' ||
                               error.response?.status >= 500;
      
      if (retryCount < 5 && isRetryableError) {
        console.log(`Login retry attempt ${retryCount + 1}/5...`);
        // Mark as connecting and wait
        setServerStatus(CONNECTION_STATUS.CONNECTING);
        await new Promise(resolve => setTimeout(resolve, 4000));
        
        // Re-check health before retry
        await checkServerHealth(1, 5);
        return login(email, password, retryCount + 1);
      }
      throw error;
    }
  };

  const signup = async (email, password, fullName) => {
    // Ensure server is connected first
    if (serverStatus !== CONNECTION_STATUS.CONNECTED) {
      await checkServerHealth(1, 10);
    }
    
    const response = await axios.post(`${API}/auth/signup`, {
      email,
      password,
      full_name: fullName
    }, { timeout: 20000 });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('demoMode');
    setToken(null);
    setUser(null);
    setIsDemo(false);
  };

  // Enter demo mode
  const enterDemoMode = () => {
    localStorage.setItem('demoMode', 'true');
    setUser(DEMO_USER);
    setIsDemo(true);
  };

  // Exit demo mode
  const exitDemoMode = () => {
    localStorage.removeItem('demoMode');
    setUser(null);
    setIsDemo(false);
  };

  const retryConnection = async () => {
    setServerStatus(CONNECTION_STATUS.CHECKING);
    await checkServerHealth();
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'ADMIN',
    isDemo,
    enterDemoMode,
    exitDemoMode,
    // New server status fields
    serverStatus,
    connectionAttempts,
    retryConnection,
    isServerConnected: serverStatus === CONNECTION_STATUS.CONNECTED,
    isServerConnecting: serverStatus === CONNECTION_STATUS.CHECKING || serverStatus === CONNECTION_STATUS.CONNECTING
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export connection status for use in components
export { CONNECTION_STATUS };

// Axios interceptor for adding auth header
export function setupAxiosInterceptors() {
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}
